// Calculate approximate bounds around center
// Adjust this value to change the area size (in km radius from center)
// Example: 5km radius = 10km x 10km total area
// Example: 1km radius = 2km x 2km total area
export function getMapBoundsFromCenter(centerLon: number, centerLat: number, radiusKm: number = 1) {
  // Convert km to degrees (approximate: 1 degree latitude ≈ 111km)
  const latDelta = radiusKm / 111;
  const lonDelta = radiusKm / (111 * Math.cos((centerLat * Math.PI) / 180)); // Adjust for latitude

  return {
    xmin: centerLon - lonDelta,
    ymin: centerLat - latDelta,
    xmax: centerLon + lonDelta,
    ymax: centerLat + latDelta,
  };
}

/**
 * Check if a tile URL contains actual data (not empty/transparent)
 *
 * @param tileUrl - URL of the tile to check
 * @param timeoutMs - Timeout in milliseconds (default: 5000)
 * @returns Promise<boolean> - true if tile has data, false otherwise
 */
export function checkTileHasData(tileUrl: string, timeoutMs: number = 5000): Promise<boolean> {
  return new Promise((resolve) => {
    // Use Image object to test if tile loads
    const img = new Image();
    img.crossOrigin = 'anonymous'; // Allow canvas access for pixel inspection

    const timeout = setTimeout(() => {
      img.src = ''; // Cancel loading
      resolve(false);
    }, timeoutMs);

    img.onload = () => {
      clearTimeout(timeout);

      try {
        // Check if image is a placeholder (1x1 or very small, or fully transparent)
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');

        if (!ctx) {
          resolve(false);

          return;
        }

        // Draw image to canvas
        ctx.drawImage(img, 0, 0);

        // Check if it's a small placeholder image (less than 10x10 pixels)
        if (img.width < 10 || img.height < 10) {
          resolve(false);

          return;
        }

        // Sample a few pixels to check if there's actual data
        // Check center and corners
        const samples = [
          ctx.getImageData(img.width / 2, img.height / 2, 1, 1).data, // center
          ctx.getImageData(0, 0, 1, 1).data, // top-left
          ctx.getImageData(img.width - 1, 0, 1, 1).data, // top-right
          ctx.getImageData(0, img.height - 1, 1, 1).data, // bottom-left
          ctx.getImageData(img.width - 1, img.height - 1, 1, 1).data, // bottom-right
        ];

        // Check if all samples are fully transparent or black
        const allTransparentOrBlack = samples.every((pixel) => {
          const [r, g, b, a] = pixel;

          return a === 0 || (r === 0 && g === 0 && b === 0);
        });

        if (allTransparentOrBlack) {
          resolve(false);
        } else {
          resolve(true);
        }
      } catch (error) {
        // If we can't inspect pixels (CORS issue), assume it has data if it loaded
        console.warn('Could not inspect tile, assuming it has data:', error);
        resolve(true);
      }
    };

    img.onerror = () => {
      clearTimeout(timeout);
      resolve(false);
    };

    img.src = tileUrl;
  });
}
